/**
 * Image Trust Guard
 * Detects seed/placeholder/mismatched images and returns a category placeholder instead.
 * Real seller-uploaded images are never overridden.
 */

// Seed image patterns — these were auto-generated during DB seeding
const SEED_IMAGE_PATTERNS = [
  /unsplash\.com/i,
  /picsum\.photos/i,
  /placeholder\.com/i,
  /via\.placeholder/i,
  /placehold\.co/i,
  /lorempixel/i,
  /dummyimage/i,
  /source\.unsplash/i,
  /images\.unsplash/i,
  /loremflickr/i,
  /randomuser\.me/i,
  /pravatar/i,
  /placekitten/i,
  /placeimg/i,
  /fakeimg/i,
];

// Category → placeholder SVG path (served from /public/assets/)
import { toAbsoluteUrl } from './media';

const CATEGORY_PLACEHOLDERS = {
  vehicles: '/assets/placeholders/vehicle.svg',
  cars: '/assets/placeholders/vehicle.svg',
  vehicles_motorbikes: '/assets/placeholders/vehicle.svg',
  auto_parts: '/assets/placeholders/vehicle.svg',
  vehicle_spare_parts: '/assets/placeholders/vehicle.svg',
  electronics: '/assets/placeholders/device.svg',
  electronics_accessories: '/assets/placeholders/device.svg',
  mobile_accessories: '/assets/placeholders/device.svg',
  phones: '/assets/placeholders/device.svg',
  laptops_accessories: '/assets/placeholders/device.svg',
  real_estate: '/assets/placeholders/house.svg',
  re_apartments: '/assets/placeholders/house.svg',
  re_houses: '/assets/placeholders/house.svg',
  re_land: '/assets/placeholders/house.svg',
  re_commercial: '/assets/placeholders/house.svg',
  jobs: '/assets/placeholders/briefcase.svg',
  services: '/assets/placeholders/tools.svg',
  fashion: '/assets/placeholders/clothing.svg',
  fashion_accessories: '/assets/placeholders/clothing.svg',
  clothing: '/assets/placeholders/clothing.svg',
  home_appliances: '/assets/placeholders/device.svg',
  furniture: '/assets/placeholders/house.svg',
  sports_equipment: '/assets/placeholders/tools.svg',
  food_beverages: '/assets/placeholders/tools.svg',
  beauty_health: '/assets/placeholders/tools.svg',
  kids_babies: '/assets/placeholders/clothing.svg',
  books_stationery: '/assets/placeholders/briefcase.svg',
  pets: '/assets/placeholders/tools.svg',
  // Ladies Hub categories
  beauty: '/assets/placeholders/beauty.svg',
  beauty_health: '/assets/placeholders/beauty.svg',
  beauty_personal_care: '/assets/placeholders/beauty.svg',
  jewellery: '/assets/placeholders/jewellery.svg',
  events: '/assets/placeholders/events.svg',
  handmade_culture: '/assets/placeholders/handmade.svg',
  womens_fashion: '/assets/placeholders/clothing.svg',
};

// Default placeholder when category doesn't match
const DEFAULT_PLACEHOLDER = '/assets/placeholders/default.svg';

/**
 * Check if an image URL is a seed/stock/placeholder image.
 */
function isSeedImage(url) {
  if (!url) return true;
  return SEED_IMAGE_PATTERNS.some(pattern => pattern.test(url));
}

/**
 * Check if an image was uploaded by a real user.
 * Real uploads go through /uploads/ or are stored via our API.
 */
function isRealUpload(url) {
  if (!url) return false;
  // Real uploads are stored in our /uploads/ directory
  if (url.includes('/uploads/')) return true;
  // Or are base64 data URIs (inline uploads)
  if (url.startsWith('data:image/')) return true;
  return false;
}

/**
 * Get the category placeholder for a given category.
 */
export function getCategoryPlaceholder(categoryId) {
  return CATEGORY_PLACEHOLDERS[categoryId] || DEFAULT_PLACEHOLDER;
}

/**
 * Apply image trust guard to a single image URL.
 * Returns the original URL if it's a real upload, or a category placeholder if it's seed/stock.
 *
 * @param {string} imageUrl - The image URL to check
 * @param {string} categoryId - The ad's category (for placeholder selection)
 * @returns {string|null} - Trusted image URL or category placeholder
 */
export function guardImage(imageUrl, categoryId) {
  // No image at all → return placeholder
  if (!imageUrl) return getCategoryPlaceholder(categoryId);

  // Real upload → trust + normalize to absolute URL.
  // Bug fix 2026-05-26 (production image pipeline):
  // Backend returns relative paths like `/api/uploads/abc.jpg`. In dev/preview
  // the frontend & backend shared one origin so the relative URL worked by
  // accident. In production they live on different hosts, so we MUST prepend
  // REACT_APP_BACKEND_URL via toAbsoluteUrl().
  if (isRealUpload(imageUrl)) return toAbsoluteUrl(imageUrl);

  // Seed/stock image → replace with category placeholder
  if (isSeedImage(imageUrl)) return getCategoryPlaceholder(categoryId);

  // External URL that isn't a known seed source → trust it (user-provided URL)
  return imageUrl;
}

/**
 * Apply image trust guard to an array of images.
 * Returns first trusted image or category placeholder.
 */
export function guardFirstImage(images, categoryId) {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return getCategoryPlaceholder(categoryId);
  }
  return guardImage(images[0], categoryId);
}
